import config from '@root/config'
import type { MarketplaceIntegration } from '@/types/marketplace'
import { getUxModuleSettings, UxIcon } from './uxSettings'
import type { UxPageOtherIntegrationRef } from './uxSettings'

export interface OtherIntegrationItem {
    id: string
    name: string
    icon: UxIcon
    priceLabel: string
    isFree: boolean
    ratingLabel: string
    link: string
}

function formatIntegrationPrice(integration: MarketplaceIntegration): { label: string; isFree: boolean } {
    if (!integration.price) {
        return { label: 'Бесплатно', isFree: true }
    }

    const rawPeriod = (integration.pricePeriod || '').toString().trim().toLowerCase()
    const amount = Number(integration.price).toLocaleString('ru-RU')

    if (rawPeriod === '0' || rawPeriod === 'once') {
        return { label: `От ${amount} ₽`, isFree: false }
    }

    return { label: `От ${amount} ₽/мес`, isFree: false }
}

function resolveIntegrationLink(integrationId: string, projectId?: string): string {
    if (projectId) {
        return `/project/${projectId}/marketplace/integration/${integrationId}`
    }

    return `/marketplace/integration/${integrationId}`
}

function resolveIntegrationIcon(integrationId: string, integration?: MarketplaceIntegration): UxIcon {
    const uxIcon = getUxModuleSettings(integrationId)?.page?.headerCard?.icon
    if (uxIcon) return uxIcon

    const extraIcon = integration?.extra?.icon || integration?.banner?.icon
    if (typeof extraIcon === 'string' && extraIcon.trim()) {
        return extraIcon.trim()
    }

    return '📦'
}

export function buildOtherIntegrationItems(
    refs: UxPageOtherIntegrationRef[],
    integrations: MarketplaceIntegration[],
    currentIntegrationId: string,
    projectId?: string,
): OtherIntegrationItem[] {
    const normalizedCurrentId = String(currentIntegrationId)

    return refs
        .filter(ref => String(ref.id) !== normalizedCurrentId)
        .map(ref => {
            const integration = integrations.find(item => String(item.id) === String(ref.id))
            if (!integration) return null

            const priceFromApi = formatIntegrationPrice(integration)
            const priceLabel =
                ref.price === null || ref.price === undefined ? priceFromApi.label : ref.price

            const ratingLabel =
                ref.rating === null || ref.rating === undefined
                    ? integration.feedbackCount
                        ? Number(integration.feedbackRating || 0).toFixed(1)
                        : ''
                    : ref.rating

            return {
                id: String(integration.id),
                name: integration.name,
                icon: resolveIntegrationIcon(String(integration.id), integration),
                priceLabel,
                isFree: priceLabel === 'Бесплатно',
                ratingLabel,
                link: resolveIntegrationLink(String(integration.id), projectId),
            }
        })
        .filter((item): item is OtherIntegrationItem => Boolean(item))
}

export const DEFAULT_OTHER_INTEGRATIONS_TITLE = 'Другие решения маркетплейса'

export const MARKETPLACE_MODULE_IDS = config.marketplaceModulesIds[config.buildType]
